import { errorMessage } from '../i18n/errorMessages';
import { formatFileSize } from '../i18n/fileSize';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useState, useEffect, useCallback, useRef } from 'react';
import type { ChannelFile } from '../types';
import * as api from '../lib/api';
import {
  classifyPreviewFile,
  decodeTextPreview,
  DOCX_PREVIEW_BYTE_LIMIT,
  InvalidTextEncodingError,
  TEXT_PREVIEW_BYTE_LIMIT,
} from '../lib/filePreview';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { ImageViewer } from './viewers/ImageViewer';
import { MarkdownViewer } from './viewers/MarkdownViewer';
import { CodeViewer } from './viewers/CodeViewer';
import { TextViewer } from './viewers/TextViewer';
import { PdfViewer } from './viewers/PdfViewer';
import { DocxViewer } from './viewers/DocxViewer';

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function FileViewer({
  file,
  channelId,
  onClose,
  onEdit,
}: {
  file: ChannelFile;
  channelId: string;
  onClose: () => void;
  onEdit?: (file: ChannelFile, content: string) => void;
}) {
  const { t, i18n } = useLingui();
  const previewKind = classifyPreviewFile(file);
  const [content, setContent] = useState<string | null>(null);
  const [docxBytes, setDocxBytes] = useState<Uint8Array | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<MessageDescriptor | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const downloadControllerRef = useRef<AbortController | null>(null);
  const downloadUrlsRef = useRef(new Set<string>());
  const mountedRef = useRef(true);

  useDismissibleLayer({ active: true, within: overlayRef, onEscape: onClose });

  useEffect(() => {
    const controller = new AbortController();
    let imageUrl: string | null = null;

    setContent(null);
    setDocxBytes(null);
    setBlobUrl(null);
    setTruncated(false);
    setError(null);

    if (previewKind === 'pdf' && file.size_bytes === 0) {
      setLoading(false);
      setError(msg`This PDF is damaged or is not a valid PDF`);
      return () => controller.abort();
    }
    if (previewKind === 'docx' && file.size_bytes === 0) {
      setLoading(false);
      setError(msg`This DOCX file is damaged or cannot be previewed`);
      return () => controller.abort();
    }
    if (previewKind === 'pdf' || previewKind === 'unsupported') {
      setLoading(false);
      return () => controller.abort();
    }
    if (
      file.size_bytes === 0 &&
      (previewKind === 'text' || previewKind === 'markdown' || previewKind === 'code')
    ) {
      setContent('');
      setLoading(false);
      return () => controller.abort();
    }
    if (previewKind === 'docx' && file.size_bytes > DOCX_PREVIEW_BYTE_LIMIT) {
      setLoading(false);
      setError(msg`This file is too large to preview safely`);
      return () => controller.abort();
    }

    setLoading(true);
    void (async () => {
      // A discarded effect must not start and abort an HTTP cache revalidation.
      await Promise.resolve();
      if (controller.signal.aborted) return;
      try {
        const isTextPreview = previewKind === 'text' || previewKind === 'markdown' || previewKind === 'code';
        const limit = isTextPreview
          ? TEXT_PREVIEW_BYTE_LIMIT
          : previewKind === 'docx'
            ? DOCX_PREVIEW_BYTE_LIMIT
            : null;
        const response = await api.downloadChannelFile(channelId, file.id, {
          signal: controller.signal,
          range: limit === null ? undefined : { start: 0, endInclusive: limit },
        });
        if (controller.signal.aborted) return;

        if (previewKind === 'image') {
          const byteBuffer = new Uint8Array(response.bytes).buffer;
          imageUrl = URL.createObjectURL(new Blob([byteBuffer], { type: file.mime_type ?? undefined }));
          setBlobUrl(imageUrl);
        } else if (isTextPreview) {
          const wasTruncated =
            file.size_bytes > TEXT_PREVIEW_BYTE_LIMIT ||
            response.bytes.byteLength > TEXT_PREVIEW_BYTE_LIMIT;
          const previewBytes = response.bytes.slice(0, TEXT_PREVIEW_BYTE_LIMIT);
          setContent(decodeTextPreview(previewBytes, wasTruncated));
          setTruncated(wasTruncated);
        } else if (previewKind === 'docx') {
          if (response.bytes.byteLength > DOCX_PREVIEW_BYTE_LIMIT) {
            setError(msg`This file is too large to preview safely`);
          } else {
            setDocxBytes(response.bytes);
          }
        }
      } catch (loadError) {
        if (controller.signal.aborted || isAbortError(loadError)) return;
        if (loadError instanceof InvalidTextEncodingError) {
          setError(msg`This file is not valid UTF-8 text`);
        } else {
          setError(errorMessage(loadError, msg`Failed to load file`));
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => {
      controller.abort();
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [
    channelId,
    file.id,
    file.mime_type,
    file.size_bytes,
    previewKind,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      downloadControllerRef.current?.abort();
      for (const url of downloadUrlsRef.current) URL.revokeObjectURL(url);
      downloadUrlsRef.current.clear();
    };
  }, []);

  const download = useCallback(async () => {
    downloadControllerRef.current?.abort();
    const controller = new AbortController();
    downloadControllerRef.current = controller;
    setDownloading(true);
    setDownloadError(null);

    try {
      const response = await api.downloadChannelFile(channelId, file.id, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const byteBuffer = new Uint8Array(response.bytes).buffer;
      const url = URL.createObjectURL(new Blob([byteBuffer], {
        type: file.mime_type || 'application/octet-stream',
      }));
      downloadUrlsRef.current.add(url);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = file.name;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      queueMicrotask(() => {
        URL.revokeObjectURL(url);
        downloadUrlsRef.current.delete(url);
      });
    } catch (downloadFailure) {
      if (mountedRef.current && !controller.signal.aborted && !isAbortError(downloadFailure)) {
        setDownloadError(errorMessage(downloadFailure, msg`Failed to download file`));
      }
    } finally {
      if (downloadControllerRef.current === controller) {
        downloadControllerRef.current = null;
        if (mountedRef.current) setDownloading(false);
      }
    }
  }, [channelId, file.id, file.mime_type, file.name]);

  const handleEdit = () => {
    if (onEdit && content !== null && !truncated) onEdit(file, content);
  };

  const renderContent = () => {
    if (loading) return <div className="file-viewer-loading"><Trans>Loading…</Trans></div>;
    if (error) {
      return (
        <div className="file-viewer-error" role="alert">
          <p>{i18n._(error)}</p>
          <button type="button" onClick={download}><Trans>Download file</Trans></button>
        </div>
      );
    }

    if (previewKind === 'image' && blobUrl) {
      return <ImageViewer url={blobUrl} name={file.name} />;
    }
    if (previewKind === 'pdf') {
      return <PdfViewer source={api.channelFileContentSource(channelId, file.id)} />;
    }
    if (previewKind === 'docx' && docxBytes) {
      return <DocxViewer bytes={docxBytes} />;
    }
    if (previewKind === 'markdown' && content !== null) {
      return (
        <>
          {truncated && <div className="file-viewer-truncated" role="status"><Trans>Only the first 1 MB is shown. Download the file to view all content.</Trans></div>}
          <MarkdownViewer content={content} onEdit={onEdit && !truncated ? handleEdit : undefined} />
        </>
      );
    }
    if (previewKind === 'code' && content !== null) {
      return (
        <>
          {truncated && <div className="file-viewer-truncated" role="status"><Trans>Only the first 1 MB is shown. Download the file to view all content.</Trans></div>}
          <CodeViewer content={content} filename={file.name} />
        </>
      );
    }
    if (previewKind === 'text' && content !== null) {
      return (
        <>
          {truncated && <div className="file-viewer-truncated" role="status"><Trans>Only the first 1 MB is shown. Download the file to view all content.</Trans></div>}
          <TextViewer content={content} />
        </>
      );
    }

    return (
      <div className="file-viewer-binary">
        <p><Trans>Preview is not supported for this file type</Trans></p>
        <p className="file-viewer-meta">{file.name} · {formatFileSize(i18n, file.size_bytes)}</p>
        <button type="button" onClick={download}><Trans>Download file</Trans></button>
      </div>
    );
  };

  return (
    <div ref={overlayRef} className="file-viewer-overlay" onClick={onClose}>
      <div className="file-viewer-panel" onClick={e => e.stopPropagation()}>
        <div className="file-viewer-header">
          <span className="file-viewer-title">{file.name}</span>
          <span className="file-viewer-size">{formatFileSize(i18n, file.size_bytes)}</span>
          <button
            type="button"
            className="file-viewer-download"
            disabled={downloading}
            onClick={download}
          >
            {downloading ? <Trans>Downloading…</Trans> : <Trans>Download</Trans>}
          </button>
          <button aria-label={t`Close file preview`} className="file-viewer-close" onClick={onClose}>✕</button>
        </div>
        {downloadError && <div className="file-viewer-download-error" role="alert">{i18n._(downloadError)}</div>}
        <div className="file-viewer-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
